import { fetchOpenAIText } from "../utils/openaiClient";

export function useCreateDialogueGeneratorLogic({
  createDialogueInput, setCreateDialogueInput, createDialogueLevel, createDialogueSpeakers,
  setCreateDialogueLoading, generatedCreateDialogue, setGeneratedCreateDialogue,
  editor, activeEditor, currentTab, onInsertToBoard,
  setShowCreateDialogueGenerator, setSelectedTemplate,
}) {
  const handleCreateDialogueGenerate = async () => {
    if (!createDialogueInput.trim()) return;

    setCreateDialogueLoading(true);
    try {
      const prompt = `Create a natural, engaging dialogue on the following topic. The dialogue should be appropriate for ${createDialogueLevel} level students. Make it conversational and include exactly ${createDialogueSpeakers} ${createDialogueSpeakers === 1 ? 'speaker' : 'speakers'} with realistic exchanges. Use real first names for speakers (e.g., Anna, Mark, Elena), not generic labels like "Speaker 1".

RULE: The "topic" in the JSON must be a concise, specific title summarizing the actual generated dialogue content. Do NOT copy or echo the user input. Infer a short topic from the dialogue itself (e.g., "Planning a Weekend Trip"), 3–9 words.

IMPORTANT: Return ONLY valid JSON in this exact format:
{
  "title": "Create a Dialogue on Any Topic",
  "level": "${createDialogueLevel}",
  "topic": "Concise topic of the generated dialogue (do not copy input)",
  "dialogue": "Anna: Hello! How are you today?\nMark: I'm doing great, thanks for asking. How about you?\nAnna: I'm good too. What have you been up to lately?\nMark: Well, I've been working on a new project..."
}

The dialogue field must be a STRING with speaker names followed by colons and their dialogue, separated by newlines (\\n). Do NOT return the dialogue as an object with speaker names as keys. Use exactly ${createDialogueSpeakers} distinct NATURAL FIRST NAMES (e.g., Anna, Mark, Elena). Do NOT use labels like "Speaker 1", "Person A", or role-only labels.

Topic: ${createDialogueInput}`;



      const contentRaw = await fetchOpenAIText({ prompt, temperature: 0.7, maxTokens: 1000 });

      if (contentRaw) {
        try {
          let content = contentRaw.trim();
          // Удаляем markdown-блоки, если есть
          if (content.startsWith('```json')) {
            content = content.replace(/^```json\n/, '').replace(/\n```$/, '');
          } else if (content.startsWith('```')) {
            content = content.replace(/^```\n/, '').replace(/\n```$/, '');
          }
          const parsed = JSON.parse(content);

          if (parsed.dialogue && parsed.topic) {
            setGeneratedCreateDialogue(parsed);

          }
        } catch (parseError) {
          // Error parsing Create Dialogue JSON
        }
      }
    } catch (e) {
      // Error in handleCreateDialogueGenerate
    } finally {
      setCreateDialogueLoading(false);
    }
  };

  const handleCreateDialogueInsert = () => {
    if (!editor || !generatedCreateDialogue) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedCreateDialogue, 'createdialogue');
      setShowCreateDialogueGenerator(false);
      setCreateDialogueInput("");
      setGeneratedCreateDialogue(null);
      setSelectedTemplate(null);
      return;
    }
    // Ensure dialogue is always a string
    const formatDialogue = (dialogueData) => {
      if (typeof dialogueData === 'string') {
        return dialogueData;
      }
      if (typeof dialogueData === 'object' && dialogueData !== null) {
        // If it's an object, convert it to a string format
        return Object.entries(dialogueData)
          .map(([speaker, text]) => `${speaker}: ${text}`)
          .join('\n');
      }
      return '';
    };

    activeEditor.chain().focus().insertCreateDialogueExercise({
      dialogue: formatDialogue(generatedCreateDialogue.dialogue),
      title: generatedCreateDialogue.title,
      level: generatedCreateDialogue.level,
      topic: generatedCreateDialogue.topic
    }).run();

    setShowCreateDialogueGenerator(false);
    setCreateDialogueInput("");
    setGeneratedCreateDialogue(null);
    setSelectedTemplate(null);
  };

  const handleCreateDialogueCancel = () => {
    setShowCreateDialogueGenerator(false);
    setCreateDialogueInput("");
    setGeneratedCreateDialogue(null);
    setSelectedTemplate(null);
  };

  return { handleCreateDialogueGenerate, handleCreateDialogueInsert, handleCreateDialogueCancel };
}
